import { useEffect, useMemo, useState } from 'react'
import { IHeaderParams } from 'ag-grid-community'
import { Fade } from '@mui/material'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import {
  GanttDisplayUnit,
  GanttParameterVO,
} from '../../../../../../../../domain/value-object/GanttParameterVO'
import { CalendarDateVO } from '../../../../../../../../domain/value-object/CalendarDateVO'
import { MilestoneDetail } from '../../../../../../../../domain/entity/MilestoneEntity'
import { intl } from '../../../../../../../../i18n'
import { colorPalette } from '../../../../../../../style/colorPalette'
import {
  MilestoneIcons,
  MilestoneWithPosition,
  EmptyMilestoneCell,
  AddButtonForPositionedCell,
} from './HeaderMilestoneIcons'

const DAY_OF_WEEK_LABEL_KEYS = [
  'dayOfWeek.short.sunday',
  'dayOfWeek.short.monday',
  'dayOfWeek.short.tuesday',
  'dayOfWeek.short.wednesday',
  'dayOfWeek.short.thursday',
  'dayOfWeek.short.friday',
  'dayOfWeek.short.saturday',
]

const calcTodayOffsetPercent = (
  unit: GanttDisplayUnit,
  today: CalendarDateVO,
  date: CalendarDateVO,
  nextScaleDate?: CalendarDateVO
): number | undefined => {
  if (unit === GanttDisplayUnit.DAY) return undefined
  if (unit === GanttDisplayUnit.WEEK) {
    const rangeStartMs = date.date.getTime()
    const rangeEndMs = nextScaleDate
      ? nextScaleDate.date.getTime()
      : rangeStartMs + 7 * 24 * 60 * 60 * 1000
    const halfDay = 12 * 60 * 60 * 1000
    return (
      ((today.date.getTime() + halfDay - rangeStartMs) /
        (rangeEndMs - rangeStartMs)) *
      100
    )
  }
  if (unit === GanttDisplayUnit.MONTH) {
    const todayDay = today.date.getDate()
    const daysInMonth = new Date(
      today.date.getFullYear(),
      today.date.getMonth() + 1,
      0
    ).getDate()
    return ((todayDay - 0.5) / daysInMonth) * 100
  }
  if (unit === GanttDisplayUnit.QUARTER) {
    const rangeStartMs = date.date.getTime()
    const rangeEndMs = nextScaleDate
      ? nextScaleDate.date.getTime()
      : rangeStartMs + 91 * 24 * 60 * 60 * 1000
    const halfDay = 12 * 60 * 60 * 1000
    return (
      ((today.date.getTime() + halfDay - rangeStartMs) /
        (rangeEndMs - rangeStartMs)) *
      100
    )
  }
  return undefined
}

type HeaderLabel = {
  date: CalendarDateVO
  label: string
  length: number
}

type MilestonesForDateResult = {
  milestones: MilestoneWithPosition[]
  positioned: boolean
}

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const useMilestonesForDate = (
  milestones: MilestoneDetail[] | undefined,
  unit: GanttDisplayUnit | undefined
): ((
  date: CalendarDateVO,
  nextDate?: CalendarDateVO
) => MilestonesForDateResult) => {
  return useMemo(() => {
    const milestoneList: MilestoneDetail[] = milestones ?? []
    if (milestoneList.length === 0) {
      return () => ({ milestones: [], positioned: false })
    }

    if (unit === GanttDisplayUnit.DAY) {
      const map = new Map<string, MilestoneDetail>()
      for (const milestone of milestoneList) {
        const dateKey = formatDateKey(new Date(milestone.milestoneDate))
        map.set(dateKey, milestone)
      }
      return (date: CalendarDateVO) => {
        const found = map.get(formatDateKey(date.date))
        return {
          milestones: found ? [{ milestone: found }] : [],
          positioned: false,
        }
      }
    }

    return (date: CalendarDateVO, nextDate?: CalendarDateVO) => {
      const rangeStart = date.date.getTime()
      let rangeEnd: number

      if (nextDate) {
        rangeEnd = nextDate.date.getTime()
      } else if (unit === GanttDisplayUnit.MONTH) {
        const lastDay = new Date(
          date.date.getFullYear(),
          date.date.getMonth() + 1,
          0
        )
        rangeEnd = lastDay.getTime() + 24 * 60 * 60 * 1000
      } else if (unit === GanttDisplayUnit.QUARTER) {
        rangeEnd = rangeStart + 91 * 24 * 60 * 60 * 1000
      } else {
        rangeEnd = rangeStart + 7 * 24 * 60 * 60 * 1000
      }

      const rangeLength = rangeEnd - rangeStart

      const found = milestoneList.filter(milestone => {
        const milestoneTime = new Date(milestone.milestoneDate).getTime()
        return milestoneTime >= rangeStart && milestoneTime < rangeEnd
      })

      return {
        milestones: found.map(milestone => {
          const milestoneTime = new Date(milestone.milestoneDate).getTime()
          const position = ((milestoneTime - rangeStart) / rangeLength) * 100
          return {
            milestone,
            position: Math.min(Math.max(position, 5), 95),
          }
        }),
        positioned: true,
      }
    }
  }, [milestones, unit])
}

const GanttHeader = injectIntl(
  (props: IHeaderParams & WrappedComponentProps) => {
    const [fade, setFade] = useState(false)
    useEffect(() => {
      setTimeout(() => setFade(true), 50)
    })

    const {
      timeScale: contextTimeScale = [],
      ganttDisplayTimeScale: displayTimeScale = [],
      ganttParameter: parameter,
      milestones: contextMilestones,
      isMilestoneEnabled,
      onOpenMilestoneDialogWithDate,
      onOpenMilestoneDialogWithUuid,
    } = props.context
    const timeScale: CalendarDateVO[] = contextTimeScale
    const today = CalendarDateVO.of(new Date())

    const getMilestonesForDate = useMilestonesForDate(
      contextMilestones,
      parameter?.unit
    )

    const dayOfWeekLabels = useMemo(
      () =>
        DAY_OF_WEEK_LABEL_KEYS.map(key =>
          props.intl.formatMessage({ id: key })
        ),
      [parameter?.unit]
    )

    const monthTimeScale: HeaderLabel[] = useMemo(() => {
      if (!parameter?.unit) return []
      const { unit } = parameter
      if (!GanttParameterVO.isMonthOrQuarterUnit(unit)) {
        return displayTimeScale.map(group => ({
          date: group,
          label: group.format('D'),
          length: 0,
        }))
      }
      return displayTimeScale.map((group, i) => {
        const daysOfPeriod = timeScale.filter(
          date =>
            group.isEqual(date) ||
            (displayTimeScale[i + 1] &&
              date.isBetween(group, displayTimeScale[i + 1]))
        )
        const label =
          unit === GanttDisplayUnit.QUARTER
            ? `Q${Math.floor(group.date.getMonth() / 3) + 1}`
            : group.date.getMonth() + 1 + intl.formatMessage({ id: 'month.ja' })
        return {
          date: group,
          label,
          length: daysOfPeriod.length,
        }
      })
    }, [timeScale, parameter])

    return (
      <Fade in={fade} timeout={300}>
        <div className="sevend-ag-cell-gantt-header">
          {monthTimeScale.map(
            ({ date, label, length }: HeaderLabel, i: number) => {
              const isToday =
                today.isEqual(date) ||
                (parameter.unit !== GanttDisplayUnit.DAY &&
                  displayTimeScale[i + 1] &&
                  today.isBetween(date, displayTimeScale[i + 1]) &&
                  !today.isEqual(displayTimeScale[i + 1]))
              const todayOffsetPercent = isToday
                ? calcTodayOffsetPercent(
                    parameter.unit,
                    today,
                    date,
                    displayTimeScale[i + 1]
                  )
                : undefined
              const isMonthOrQuarter = GanttParameterVO.isMonthOrQuarterUnit(
                parameter.unit
              )
              const isHoliday =
                !isMonthOrQuarter && date.isHoliday() && !date.isSaturday()
              const isSaturday = !isMonthOrQuarter && date.isSaturday()
              const color = isHoliday
                ? colorPalette.pink[6]
                : isSaturday
                  ? colorPalette.skyBlue[8]
                  : undefined
              const labelStyle = {
                fontSize: '12px',
                fontFamily: 'Noto Sans JP',
              }
              const dayStyle = {
                fontSize: '10px',
                fontFamily: 'Noto Sans JP',
              }
              const isFirstday = !isMonthOrQuarter && date.isFirstday()
              const isFirstmonth = isMonthOrQuarter && date.isFirstmonth()
              const isFirstweek =
                parameter.unit === GanttDisplayUnit.WEEK && date.isFirstweek()
              const nextDate = displayTimeScale[i + 1]
              const dateMilestones = getMilestonesForDate(date, nextDate)

              return (
                <div
                  key={`gantt-header-${i}`}
                  className={`sevend-ag-cell-gantt-header__date-header ${
                    isToday
                      ? 'sevend-ag-cell-gantt-header__date-header--today'
                      : ''
                  } ${
                    isHoliday || isSaturday
                      ? 'sevend-ag-cell-gantt-header__date-header--holiday'
                      : ''
                  }  ${
                    isFirstmonth || isFirstday || isFirstweek
                      ? 'sevend-ag-cell-gantt-header__date-header__first'
                      : ''
                  }`}
                  style={{
                    borderColor: colorPalette.monotone[2],
                    fontSize: '12px',
                    ...(isMilestoneEnabled && {
                      justifyContent: 'flex-start',
                      paddingTop: 8,
                    }),
                    width: isMonthOrQuarter
                      ? `${(length / timeScale.length) * 100}%`
                      : '100%',
                    ...(todayOffsetPercent != null && {
                      ['--today-offset' as string]: `${todayOffsetPercent}%`,
                    }),
                  }}
                >
                  {dateMilestones.milestones.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: dateMilestones.positioned ? 10 : 2,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <MilestoneIcons
                        milestones={dateMilestones.milestones}
                        positioned={dateMilestones.positioned}
                        showMilestoneName={
                          parameter?.displayOptions?.showMilestoneName ?? true
                        }
                        milestoneNameResetSeq={
                          parameter?.displayOptions?.milestoneNameResetSeq ?? 0
                        }
                        onEdit={
                          onOpenMilestoneDialogWithUuid
                            ? uuid => onOpenMilestoneDialogWithUuid(uuid)
                            : undefined
                        }
                      />
                    </div>
                  )}
                  {isMilestoneEnabled && dateMilestones.positioned && (
                    <AddButtonForPositionedCell
                      onAdd={() =>
                        onOpenMilestoneDialogWithDate(formatDateKey(date.date))
                      }
                    />
                  )}
                  {isMilestoneEnabled &&
                    !dateMilestones.positioned &&
                    dateMilestones.milestones.length === 0 && (
                      <EmptyMilestoneCell
                        onAdd={() =>
                          onOpenMilestoneDialogWithDate(
                            formatDateKey(date.date)
                          )
                        }
                      />
                    )}
                  <span
                    style={{
                      color,
                      ...labelStyle,
                    }}
                  >
                    {label}
                  </span>
                  {parameter.unit === GanttDisplayUnit.DAY && (
                    <span style={{ color, ...dayStyle }}>
                      {dayOfWeekLabels[date.getDayOfWeek()]}
                    </span>
                  )}
                </div>
              )
            }
          )}
        </div>
      </Fade>
    )
  }
)

export default GanttHeader
